import { MutableRefObject, useEffect } from 'react';
import { useIntersection } from 'react-use';

export type UseInfiniteScrollingConfig = {
  /** Whether additional data is available */
  canFetch: boolean;
  /** Called when the `trigger` element is intersecting the container view */
  onFetch: () => void;
  /** Whether data is currently being fetched.
   * If not set, `onFetch` will be called regardless if data is being fetched */
  isFetching?: boolean;
  /** Percentage of the `trigger`'s visibility the callback should be executed  */
  threshold?: number;
  /** Margin around the `container` */
  rootMargin?: `${number}${'px' | '%'}`;
  /** Trigger element ref */
  trigger: MutableRefObject<HTMLElement | null>;
  /** Container element ref */
  container: MutableRefObject<HTMLElement | null>;
};

export const useInfiniteScrolling = ({
  trigger,
  container,
  onFetch,
  canFetch,
  isFetching = false,
  threshold = 0.5,
  rootMargin = '0px',
}: UseInfiniteScrollingConfig) => {
  /**
   * @see https://github.com/streamich/react-use/blob/master/src/useIntersection.ts
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
   */
  const intersection = useIntersection(trigger, { threshold, rootMargin, root: container.current });

  const handleIntersectionChange = () => {
    const hasElements = container.current && trigger.current;
    if (canFetch && !isFetching && hasElements && intersection?.isIntersecting) {
      onFetch();
    }
  };

  /** Note: `intersection` is intentionally the only dependency here;
   *  We only want to react to `intersection` changes */
  useEffect(() => {
    handleIntersectionChange();
  }, [intersection]);

  return { intersection };
};
